import React, { useCallback, useRef, useState } from 'react';
import { ConfigProvider, DatePicker } from 'tea-component';
import { useIntl } from 'umi';
import moment, { Moment } from 'moment';
import en_US from 'tea-component/es/i18n/locale/en_US';
import zh_CN from 'tea-component/es/i18n/locale/zh_CN';
import { LANGUAGE_EN } from '@home/_config/config';
import TimePicker from '@/pages/dashboard/components/TimePicker';
import HomeRangePicker from '@home/components/RangePicker';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import type {
  IKPIFilterProps,
  TDateInputValue,
  TRangeValue,
} from './type';
import {
  useApiDefaultDateInfo,
  useDefaultDayRange,
  useDefaultMonthRange,
  useInitFilterValue,
} from './hooks';
import {
  getDayAsYtdPayload,
  getDayPayload,
  getDayRangePayload,
  getMonthPayload,
  getMonthRangePayload,
  getHistoricalMonthSelectableBounds,
  getHistoricalDaySelectableBounds,
  normalizeDayPickerValue,
} from './utils';

import './index.scss';
import classNames from 'classnames';

const { MonthPicker } = DatePicker;

export default function KPIFilter(props: IKPIFilterProps) {
  const { getData, config, className, style, data } = props;

  const { dateFilterConfig } = config || {};
  const {
    pickerType = 'day',
    dateSubtract = 1,
    disabledStart = '2025-01-01',
    timeZone = 0,
    startSubtract,
    endSubtract,
    defaultDateFromApi,
    dayAsYtd = false,
    disableCurrent = false,
    disableFuture = false,
  } = dateFilterConfig || {};

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const [timeValue, setTimeValue] = useState<string>('');
  const [monthValue, setMonthValue] = useState<string>('');
  const [monthRange, setMonthRange] = useState<TRangeValue>([]);
  const [dayRange, setDayRange] = useState<TRangeValue>([]);
  const hasInitializedRef = useRef<boolean>(false);

  const { hasApiDefaultConfig, apiDefaultDate, isApiDefaultDataReady } =
    useApiDefaultDateInfo(defaultDateFromApi?.dataPath, data);

  const defaultMonthRange = useDefaultMonthRange(
    dateSubtract,
    startSubtract,
    endSubtract,
    timeZone,
  );

  const defaultDayRange = useDefaultDayRange(
    dateSubtract,
    startSubtract,
    endSubtract,
    timeZone,
  );

  const markInitialized = useCallback((): void => {
    hasInitializedRef.current = true;
  }, []);

  useInitFilterValue(
    hasInitializedRef.current,
    hasApiDefaultConfig,
    isApiDefaultDataReady,
    apiDefaultDate,
    pickerType,
    dateSubtract,
    timeZone,
    defaultMonthRange,
    setTimeValue,
    setMonthValue,
    setMonthRange,
    getData,
    markInitialized,
    disabledStart,
    defaultDayRange,
    setDayRange,
    dayAsYtd,
    disableCurrent,
    disableFuture,
  );

  /**
   * 处理日选择器变更。
   * 由于底层 Tea DatePicker 类型声明较弱，这里先做输入归一化，再交给统一日期格式化方法处理。
   */
  const handleDayChange = (val: TDateInputValue): void => {
    const normalizedValue = normalizeDayPickerValue(val);
    const value = dateTimeFormat(normalizedValue, 'day');
    setTimeValue(value);
    getData(dayAsYtd ? getDayAsYtdPayload(value) : getDayPayload(value));
  };

  /**
   * 处理单月选择器变更，始终回传年初到当月末。
   */
  const handleMonthChange = (val: Moment | null): void => {
    if (!val) return;
    setMonthValue(val.format('YYYY-MM'));
    getData(getMonthPayload(val));
  };

  /**
   * 处理月份范围选择器变更。
   */
  const handleMonthRangeChange = (val: TRangeValue): void => {
    if (!val || val.length < 2 || !val[0] || !val[1]) return;
    setMonthRange(val);
    getData(getMonthRangePayload(val));
  };

  /**
   * 处理日区间选择器变更。
   */
  const handleDayRangeChange = (val: TRangeValue): void => {
    if (!val || val.length < 2 || !val[0] || !val[1]) return;
    setDayRange(val);
    getData(getDayRangePayload(val));
  };

  /**
   * 限制月份选择范围：与 Tea MonthPicker / RangePicker 内 MonthTable 约定一致（返回 true 表示该月可选）。
   * 默认可选 [disabledStart, 当月]；disableCurrent 时上界为上一自然月。
   */
  const disabledMonthFn = useCallback(
    (date: Moment, _startDate?: Moment): boolean => {
      if (disableCurrent || disableFuture) {
        const { lowerBound, upperBound } = getHistoricalMonthSelectableBounds(
          disabledStart,
          timeZone,
          disableCurrent,
          disableFuture,
        );
        return !(
          date.isBefore(lowerBound, 'month') ||
          date.isAfter(upperBound, 'month')
        );
      }
      const startMonth = moment(disabledStart).startOf('month');
      const currentMonth = moment().utcOffset(timeZone).startOf('month');
      return !(
        date.isBefore(startMonth, 'month') ||
        date.isAfter(currentMonth, 'month')
      );
    },
    [disableCurrent, disableFuture, disabledStart, timeZone],
  );

  /**
   * 限制 day 选择范围（返回 true 表示该日可选）。
   * 默认可选 [disabledStart, 今天]；disableCurrent 时上界为昨天；disableFuture 时禁用未来。
   */
  const disabledDayFn = useCallback(
    (date: Moment): boolean => {
      if (disableCurrent || disableFuture) {
        const { lowerBound, upperBound } = getHistoricalDaySelectableBounds(
          disabledStart,
          timeZone,
          disableCurrent,
          disableFuture,
        );
        return !(
          date.isBefore(lowerBound, 'day') ||
          date.isAfter(upperBound, 'day')
        );
      }
      const startDate = moment(disabledStart);
      const today = moment().utcOffset(timeZone);
      return !(date.isBefore(startDate, 'day') || date.isAfter(today, 'day'));
    },
    [disableCurrent, disableFuture, disabledStart, timeZone],
  );

  const monthPickerClassName = classNames(
    isEn ? 'dbh-studio-month-spicker-language-en' : 'dbh-studio-month-spicker-language-cn',
  );

  const dayPickerClassName = classNames(
    isEn ? 'dbh-studio-date-single-spicker-language-en' : 'dbh-studio-date-single-spicker-language-cn',
  );

  /**
   * 按选择器类型渲染不同的日期控件。
   */
  const renderPicker = (): React.ReactNode => {
    if (pickerType === 'month') {
      return (
        <ConfigProvider locale={isEn ? en_US : zh_CN}>
          <MonthPicker
            className={monthPickerClassName}
            value={monthValue ? moment(monthValue, 'YYYY-MM') : undefined}
            onChange={handleMonthChange}
            disabledMonth={disabledMonthFn}
          />
        </ConfigProvider>
      );
    }

    if (pickerType === 'monthRange') {
      return (
        <div className="search-item search-item-date">
          <HomeRangePicker
            type="month"
            typeList={['month']}
            showSelectType={false}
            showShortcut={false}
            language={isEn ? 'en' : 'cn'}
            value={monthRange.length === 2 ? monthRange : defaultMonthRange}
            onChange={handleMonthRangeChange}
            disabledMonth={disabledMonthFn}
          />
        </div>
      );
    }

    if (pickerType === 'dayRange') {
      return (
        <div className="search-item search-item-date">
          <HomeRangePicker
            type="date"
            typeList={['date']}
            showSelectType={false}
            showShortcut={false}
            language={isEn ? 'en' : 'cn'}
            value={dayRange.length === 2 ? dayRange : defaultDayRange}
            onChange={handleDayRangeChange}
            disabledDate={{
              date: disabledDayFn,
            }}
          />
        </div>
      );
    }

    return (
      <TimePicker
        matchButtonWidth
        className={dayPickerClassName}
        comKey="studio-home-kpi-timepicker"
        locale={locale}
        onConfirm={handleDayChange}
        value={timeValue}
        disabled={false}
        disabledDate={disabledDayFn}
      />
    );
  };

  return (
    <div className={classNames('dbh-studio-home-kpi-filter', className)} style={style}>
      <div className="dbh-studio-home-kpi-filter-time">
        {renderPicker()}
      </div>
    </div>
  );
}
